import type { PageSection, SidebarItem as PageSidebarItem } from '@crowdex/plugin-contracts';
import { Plus } from 'lucide-react';
import { type PointerEvent as ReactPointerEvent, useMemo } from 'react';
import {
  organizeFilter,
  SidebarError,
  SidebarGroup,
  SidebarHeader,
  SidebarItem,
  SidebarSection,
  sortFilter,
  sortSidebarEntries,
  useSidebarArrangement,
} from '@/components/sidebar/sidebar';
import {
  type AppSection,
  pickLiveItem,
  useAppSectionItems,
} from '@/features/apps/app-panel-sidebar';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { PluginPanel } from '@/features/plugins/plugin-workbench';
import { addAppPanelTab, setAppPanelTarget } from '@/lib/desktop-shell/app-panels';
import { splitPanelTab } from '@/lib/desktop-shell/panel-split';
import {
  type SidebarSearchSource,
  useSidebarSearchSource,
} from '@/lib/desktop-shell/sidebar-search';
import { useDesktopShellStore, useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { dragIntoPanelSplit } from '@/lib/side-panel/split-drag';
import { formatSidebarAge } from '@/lib/sidebar-threads';

/**
 * The app side of the sidebar while the app's tab is the one shown in the side panel of the
 * conversation on screen, laid out like the conversations: first the live sections its view
 * declares for the document it shows (a design's pages and slides), then the items of the section
 * the toggle shows (`page.sidebars`), ungrouped first, then one collapsible group per group. An
 * item shows in the app's tab of the conversation on screen; dragged over the panel, it opens in
 * another tab of the app, beside the tab it is dropped on.
 */
export function AppPanelSidebarItems({
  panel,
  tabId,
  section,
  liveSections,
}: {
  panel: PluginPanel;
  /** The app's tab shown in the panel. */
  tabId: string | null;
  /** The section the toggle shows; none when the app only has live sections. */
  section: AppSection | null;
  liveSections: readonly PageSection[];
}) {
  return (
    <>
      {tabId !== null &&
        // An empty section is not shown: no heading over nothing.
        liveSections
          .filter((live) => live.items.length > 0)
          .map((live) => <LiveSection key={live.id} panel={panel} section={live} tabId={tabId} />)}
      {section !== null && <SectionItems panel={panel} section={section} tabId={tabId ?? ''} />}
    </>
  );
}

/** A live section: its items, the one the view shows marked; a click reaches the view. */
function LiveSection({
  panel,
  tabId,
  section,
}: {
  panel: PluginPanel;
  tabId: string;
  section: PageSection;
}) {
  const grouped = new Map<string | undefined, PageSidebarItem[]>();
  for (const item of section.items)
    grouped.set(item.group, [...(grouped.get(item.group) ?? []), item]);
  const row = (item: PageSidebarItem) => (
    <SidebarItem
      active={item.id === section.selected}
      detail={item.detail}
      {...(item.icon ? { icon: pluginIcon(item.icon) } : {})}
      key={item.id}
      label={item.title}
      onSelect={() => pickLiveItem(tabId, section.id, item.id)}
      title={item.title}
    />
  );
  return (
    <SidebarGroup
      label={section.title}
      sectionProps={{ 'aria-label': `${panel.title} · ${section.title}` }}
    >
      {[...grouped].map(([group, items]) =>
        group === undefined ? (
          items.map(row)
        ) : (
          <SidebarGroup key={group} label={group}>
            {items.map(row)}
          </SidebarGroup>
        ),
      )}
    </SidebarGroup>
  );
}

/** The items a section's route lists, and "New" (the app with nothing selected). */
function SectionItems({
  panel,
  tabId,
  section,
}: {
  panel: PluginPanel;
  tabId: string;
  section: AppSection;
}) {
  const store = useDesktopShellStoreApi();
  const target = useDesktopShellStore((state) => state.appPanelTargets[tabId]);
  const item = target?.item ?? null;
  // An item is of the section it was picked in (the first one when the app did not say).
  const ofSection = (target?.section ?? section.id) === section.id;
  const sidebar = useAppSectionItems(panel, section);
  const arrangement = useSidebarArrangement(`plugin:${panel.plugin}/${panel.id}/${section.id}`);
  const items = sortSidebarEntries(sidebar.items, arrangement.sort, {
    name: (entry) => entry.title,
    updatedAt: (entry) => entry.updatedAt,
  });
  const select = (id: string | null) =>
    setAppPanelTarget(store, tabId, { item: id, section: section.id });
  const drag = (id: string, event: ReactPointerEvent<HTMLElement>) =>
    dragIntoPanelSplit(event, (preview) =>
      splitPanelTab(
        store,
        addAppPanelTab(store, panel.view, { item: id, section: section.id }),
        preview.targetId,
        preview.side,
      ),
    );
  // The sidebar's search finds the section's items while they are listed.
  const search = useMemo<SidebarSearchSource>(
    () => ({
      title: `Search ${section.title}`,
      placeholder: `Search ${section.title}…`,
      empty: 'Nothing found.',
      groups: [...new Set(sidebar.items.map((entry) => entry.group ?? section.title))].map(
        (group) => ({
          heading: group,
          items: sidebar.items
            .filter((entry) => (entry.group ?? section.title) === group)
            .map((entry) => ({
              id: entry.id,
              label: entry.title,
              ...(entry.detail ? { meta: entry.detail } : {}),
              current: ofSection && entry.id === item,
            })),
        }),
      ),
      onSelect: (entry) => setAppPanelTarget(store, tabId, { item: entry.id, section: section.id }),
    }),
    [section, sidebar.items, item, ofSection, store, tabId],
  );
  useSidebarSearchSource(search);
  const grouped = arrangement.organize === 'grouped';
  const ungrouped = items.filter((entry) => !grouped || entry.group === undefined);
  // Groups keep the order the route lists them in.
  const groups = grouped
    ? [
        ...new Set(
          sidebar.items.flatMap((entry) => (entry.group === undefined ? [] : [entry.group])),
        ),
      ]
    : [];
  const hasGroups = sidebar.items.some((entry) => entry.group !== undefined);
  const button = (entry: PageSidebarItem) => (
    <SidebarItem
      active={ofSection && entry.id === item}
      detail={
        entry.detail ??
        (entry.updatedAt === undefined ? undefined : formatSidebarAge(entry.updatedAt))
      }
      {...(entry.icon ? { icon: pluginIcon(entry.icon) } : {})}
      key={entry.id}
      label={entry.title}
      onPointerDown={(event) => drag(entry.id, event)}
      onSelect={() => select(entry.id)}
      title={entry.title}
    />
  );
  return (
    <>
      <SidebarHeader
        busy={sidebar.loading}
        filter={[
          ...(hasGroups ? [organizeFilter(arrangement.organize, arrangement.setOrganize)] : []),
          sortFilter(arrangement.sort, arrangement.setSort),
        ]}
        title={section.title}
      />
      {/* With nothing selected, the app starts something new. */}
      <SidebarSection>
        <SidebarItem active={item === null} icon={Plus} label="New" onSelect={() => select(null)} />
      </SidebarSection>
      {sidebar.error !== null && <SidebarError>{sidebar.error}</SidebarError>}
      {ungrouped.length > 0 && <SidebarSection>{ungrouped.map(button)}</SidebarSection>}
      {groups.map((group) => (
        <SidebarGroup key={group} label={group}>
          {items.filter((entry) => entry.group === group).map(button)}
        </SidebarGroup>
      ))}
    </>
  );
}
